/**
 * Liabilities Form Component
 *
 * Dynamic form for managing liabilities/debts in financial profile.
 */

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  GlassButton,
  GlassIconButton,
  GlassTooltip,
  Grid,
  MagicInput,
  MagicSelect,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useMemo } from 'react';
import { LIABILITY_TYPE_OPTIONS, type Liability } from '@/lib/constants/financial-profile';

const LIABILITY_TYPE_I18N: Record<string, string> = {
  mortgage: 'liabilities.type.mortgage',
  auto_loan: 'liabilities.type.autoLoan',
  student_loan: 'liabilities.type.studentLoan',
  credit_card: 'liabilities.type.creditCard',
  personal_loan: 'liabilities.type.personalLoan',
  heloc: 'liabilities.type.heloc',
  other: 'liabilities.type.other',
};

interface LiabilitiesFormProps {
  liabilities: Liability[];
  onAdd: (liability: Omit<Liability, 'id'>) => void;
  onUpdate: (id: string, updates: Partial<Liability>) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  maxLiabilities?: number;
}

export default function LiabilitiesForm({
  liabilities,
  onAdd,
  onUpdate,
  onRemove,
  disabled = false,
  maxLiabilities = 15,
}: LiabilitiesFormProps) {
  const { t } = useI18n();
  const translatedLiabilityOptions = useMemo(
    () =>
      LIABILITY_TYPE_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(LIABILITY_TYPE_I18N[opt.value] ?? opt.label),
      })),
    [t],
  );
  const handleAddLiability = () => {
    if (liabilities.length < maxLiabilities) {
      onAdd({
        liabilityType: 'credit_card',
        creditor: '',
        monthlyPayment: 0,
        balance: 0,
      });
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('liabilities.description')}
      </Typography>

      {liabilities.length === 0 && (
        <Box
          sx={{
            p: 3,
            mb: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {t('liabilities.empty')}
          </Typography>
        </Box>
      )}

      {liabilities.map((liability, index) => (
        <Box
          key={liability.id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">{t('liabilities.liabilityNumber', { number: index + 1 })}</Typography>
            <GlassTooltip title={t('liabilities.removeLiability')}>
              <GlassIconButton size="small" onClick={() => onRemove(liability.id)} color="error" disabled={disabled}>
                <DeleteIcon fontSize="small" />
              </GlassIconButton>
            </GlassTooltip>
          </Box>

          <Grid container spacing={2}>
            {/* Liability Type */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicSelect
                fullWidth
                required
                disabled={disabled}
                label={t('liabilities.liabilityType')}
                value={liability.liabilityType}
                onChange={(e) =>
                  onUpdate(liability.id, { liabilityType: e.target.value as Liability['liabilityType'] })
                }
                size="small"
                options={translatedLiabilityOptions}
              />
            </Grid>

            {/* Creditor */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                size="small"
                label={t('liabilities.creditor')}
                value={liability.creditor}
                onChange={(e) => onUpdate(liability.id, { creditor: e.target.value })}
                disabled={disabled}
                required
                placeholder={t('liabilities.creditorPlaceholder')}
              />
            </Grid>

            {/* Monthly Payment */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <MagicInput
                fullWidth
                size="small"
                label={t('liabilities.monthlyPayment')}
                type="number"
                value={liability.monthlyPayment}
                onChange={(e) => onUpdate(liability.id, { monthlyPayment: Number.parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                required
                slotProps={{
                  input: {
                    startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                    inputProps: { min: 0, step: 10 },
                  },
                }}
              />
            </Grid>

            {/* Balance */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <MagicInput
                fullWidth
                size="small"
                label={t('liabilities.totalBalance')}
                type="number"
                value={liability.balance}
                onChange={(e) => onUpdate(liability.id, { balance: Number.parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                required
                slotProps={{
                  input: {
                    startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                    inputProps: { min: 0, step: 100 },
                  },
                }}
              />
            </Grid>

            {/* Interest Rate (Optional) */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <MagicInput
                fullWidth
                size="small"
                label={t('liabilities.interestRate')}
                type="number"
                value={liability.interestRate ?? ''}
                onChange={(e) =>
                  onUpdate(liability.id, { interestRate: Number.parseFloat(e.target.value) || undefined })
                }
                disabled={disabled}
                slotProps={{
                  input: {
                    endAdornment: <span style={{ marginLeft: 8 }}>%</span>,
                    inputProps: { min: 0, max: 100, step: 0.1 },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      {liabilities.length < maxLiabilities && (
        <GlassButton
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddLiability}
          disabled={disabled}
        >
          {t('liabilities.addLiability')}
        </GlassButton>
      )}

      {liabilities.length >= maxLiabilities && (
        <Typography variant="caption" color="textSecondary">
          {t('liabilities.maxAllowed', { max: maxLiabilities })}
        </Typography>
      )}
    </Box>
  );
}
